import type { Guide } from "../types";
import { appsCoreGuides } from "./appsCore";
import { appsMoreGuides } from "./appsMore";
import { appsNewGuides } from "./appsNew";

// The App catalog. "How apps work" is authored here; each individual app has
// its own guide, split across appsCore (Basic / Live Feeds / Communication) and
// appsMore (Business / Content / Hotel) and grouped by store
// category. The active app list mirrors the store on production.
const OVERVIEW: Guide = {
  slug: "add-an-app",
  category: "apps",
  route: "/apps/add-an-app",
  title: "How apps work: add & configure an app",
  summary:
    "Browse the App Store, configure an app, save it to My Apps, and put it on a screen.",
  intro:
    "Apps are live, always-updating content — weather, clocks, menu boards, news, directories, and more. The flow is the same for every app: pick it from the App Store, fill in its settings while watching the live preview, save it, then assign it to a screen or add it to a playlist.",
  readingTime: 4,
  status: "draft",
  group: "Start here",
  lastUpdated: "2026-09-30",
  steps: [
    {
      title: "Open the App Store",
      blocks: [
        {
          type: "p",
          text: "Click Apps in the sidebar. The App Store tab lists every app you can add; the My Apps tab holds the apps you’ve already configured in this Space.",
        },
        {
          type: "p",
          text: "Use the category chips — All, Basic, Live Feeds, Social, Communication, Business, Content and Hotel — to narrow the list, or type in the Search Apps box.",
        },
        {
          type: "note",
          tone: "info",
          text: "Some apps only appear when your organisation has the matching module or feature switched on — for example Queue / Now Serving and Queue Kiosk (Queue module), Wayfinding, Hotel Directory and Campus Directory (Wayfinding module), and Power BI. If you expect an app and can’t see it, ask your administrator or reseller.",
        },
      ],
      screenshot: {
        src: "apps/add-an-app/01.png",
        alt: "The Apps page on the App Store tab, with category chips and app cards",
        caption: "The App Store, with category chips across the top.",
        todo: "Apps page, App Store tab, All selected. Current chips: All, Basic, Live Feeds, Social, Communication, Business, Content, Hotel (the old capture shows 39 apps and no Social chip).",
      },
    },
    {
      title: "Pick an app and configure it",
      blocks: [
        {
          type: "p",
          text: "Click an app card to open its editor. The settings are on the left and a live Preview is on the right — switch it between Landscape and Portrait to check how the app looks on each kind of screen. Each app has its own settings: a location for Weather, a web address for Website URL, sections and prices for Digital Menu Board, and so on.",
        },
        {
          type: "p",
          text: "Near the top of most editors a short note tells you how the app behaves if the screen loses its internet connection:",
        },
        {
          type: "ul",
          items: [
            "Works offline — the app plays from its saved settings (clocks, timers, menu boards, Follow Us, Promo Wall, Ask a Question).",
            "Works offline with the last synced data — the app refreshes when it can and keeps showing the last data it downloaded (Weather, RSS feeds, Currencies, Noticeboard).",
            "Needs internet — the app streams from an outside service and may go blank while offline (Website URL, Embed, Transport, Live Sources, Social & Reviews).",
          ],
        },
      ],
      screenshot: {
        src: "apps/add-an-app/02.png",
        alt: "An app editor with settings on the left and a live preview on the right",
        caption: "Settings on the left, live preview on the right.",
        todo: "Configure: an app editor with the connectivity note above App name and a rendered Landscape preview (currently the Digital Menu Board editor; the Weather editor needs a Google Maps key the local demo doesn't have).",
      },
    },
    {
      title: "Save it to My Apps",
      blocks: [
        {
          type: "p",
          text: "Give the app a clear App name (it’s for your team — viewers don’t see it) and click Save Changes. The app now appears on the My Apps tab, where each card has Edit to change its settings and Delete to remove it.",
        },
        {
          type: "note",
          tone: "tip",
          text: "If you manage several Spaces and haven’t switched into one, the editor asks you to choose which Space the app belongs to before it can be saved.",
        },
      ],
      screenshot: {
        src: "apps/add-an-app/03.png",
        alt: "The My Apps tab showing saved app cards with Edit and Delete buttons",
        caption: "Saved apps live on the My Apps tab.",
        todo: "My Apps tab with a few saved apps using placeholder names (e.g. “Lobby Weather”, “Reception Clock”). Avoid test names like “(live test)”, real channel names, or dev.* hostnames on the cards.",
      },
    },
    {
      title: "Put it on a screen",
      blocks: [
        {
          type: "p",
          text: "Assign the saved app to a screen the same way as any content — with Set content on the screen, or by adding it to a playlist or a channel zone.",
        },
        { type: "steps-ref", label: "See: Assign content to a screen", to: "/screens/assign-content" },
      ],
    },
    {
      title: "Let AI Studio draft content for you",
      blocks: [
        {
          type: "p",
          text: "Click AI Studio at the top of the Apps page to describe what you want and let AI create it. Choose a mode, type a prompt, and click Generate:",
        },
        {
          type: "ul",
          items: [
            "Image — a background image for a screen; click Save to media library to use it.",
            "Text — copy for a notice or promo; click Copy text.",
            "Noticeboard — a set of notices; click Create noticeboard to open them in the Noticeboard editor.",
            "Menu — a full menu; click Create menu board (optionally with generated images) to open it in the Digital Menu Board editor.",
          ],
        },
        {
          type: "note",
          tone: "info",
          text: "If AI Studio shows “AI isn’t set up yet”, an administrator still needs to connect an AI provider on the server.",
        },
      ],
      screenshot: {
        src: "apps/add-an-app/04.png",
        alt: "AI Studio showing the “AI isn’t set up yet” notice",
        caption: "Until an AI provider is connected, AI Studio shows this notice.",
        todo: "Apps → AI Studio (/apps/ai-studio). Current capture shows the “AI isn’t set up yet” state (no AI provider in the local demo). Retake with a provider configured: the four mode buttons, a prompt typed in, and a generated result — then restore the alt/caption.",
      },
    },
    {
      title: "Can’t find the app you need?",
      blocks: [
        {
          type: "p",
          text: "Click Request App at the top of the Apps page. It opens a support request pre-filled with a short template — describe the app, what it should do, and where it will run, and attach a mockup if you have one.",
        },
      ],
    },
  ],
  related: [
    { label: "Assign content to a screen", to: "/screens/assign-content" },
    { label: "Website URL", to: "/apps/website-url" },
    { label: "Digital Menu Board", to: "/apps/digital-menu-board" },
  ],
  keywords: ["apps", "app store", "my apps", "add app", "configure", "widget", "ai studio", "request app", "offline"],
};

// Same category order as the App Store chips (the sidebar groups by first appearance).
const GROUP_ORDER = ["Start here", "Basic", "Live Feeds", "Social", "Communication", "Business", "Content", "Hotel"];
const groupRank = (g: Guide) => {
  const i = GROUP_ORDER.indexOf(g.group ?? "");
  return i === -1 ? GROUP_ORDER.length : i;
};

// Array.prototype.sort is stable, so guides keep their authored order within a group.
export const appsGuides: Guide[] = [OVERVIEW, ...appsCoreGuides, ...appsMoreGuides, ...appsNewGuides].sort(
  (a, b) => groupRank(a) - groupRank(b),
);
